import axios from "axios";
import {defineStore} from "pinia";
import {useAppState} from "../Stores/AppState";
import {useWorkspace} from "../Stores/Workspaces";
import router from "../router";
import {ROUTE_NAMES} from "../Constants/routes";

import TwilioFactory from "../Utilities/TwilioClientObserver";
import {useConversation} from "../Stores/Conversation";
import {cloneDeep} from "lodash";
import {useTeamPanel} from "./TeamPanel";
import {useUserDataKeyStore} from "./UserDataKey.js";
import Utilities from "Admin/js/Helpers/utilities.js";
import {profileRepresentsOrganizationOwner} from "Admin/js/Helpers/organizationOwnerProfile.js";
import {useWorkstationConnectStore} from "Admin/js/Stores/WorkstationConnect.js";
import {useRolesAndPermissions} from "Admin/js/Stores/RolesAndPermissions.js";
import {useAlignStore} from "Admin/js/Stores/Align.js";
import {useColorTheme} from "Admin/js/Composables/useColorTheme.js";
import {useWorkspaceTree} from "./WorkspaceTree";
import {
    subscribeImportProgressChannel,
    unsubscribeImportProgressChannel,
} from "Admin/js/Services/LiveSync/Channels/ImportProgressChannel.js";
import {TEAM_PANEL_VIEW_MODES} from "../Constants/menus.js";
import * as TTL from "./TimeTrackerConstants.js";
import {applyGenerateReportProgressCatchUp} from "../Helpers/generateReportProgressCatchUp.js";
import {downloadGeneratedReport} from "../Helpers/downloadGeneratedReport.js";
import {buildCompletedExportShiftbarNotice} from "../Helpers/exportCompletionNotice.js";
import {
    hasAutoDownloadedOperationId,
    hasDismissedOperationId,
    isCompletedOperationVisible,
    loadAutoDownloadedOperationIds,
    loadDismissedOperationIds,
    rememberAutoDownloadedOperationId,
    rememberDismissedOperationId,
    shouldDownloadExport,
} from "../Helpers/autoDownloadedOperationIds.js";

import {
    loadImportDraftProgress,
    upsertImportDraftProgress,
    removeImportDraftProgress,
    isImportDraftId,
    getImportDraftAnalysisProgressId,
    findImportDraftByAnalysisProgressId,
} from "../Helpers/importDraftProgress.js";
import { isLiveImportProgressId } from "../Helpers/importProgressDiscard.js";
import { WORK_PANEL_VIEW_MODE_FULL } from "Admin/js/Components/WorkPanel/Composables/workPanelViewMode.js";

export const useUserStore = defineStore("UserStore", {
    state: () => ({
        user: {
            id: "",
            name: "",
            email: "",
            country: "",
        },
        profile: {
            id: "",
            name: "",
            email: "",
        },
        user_data: null,
        newInvitedMember: false,
        newAssignMember: false,
        users: [],
        own_profiles: [],
        profiles: [],
        inactive_profiles: [],
        assigned_users: null,
        user_search_key: null,
        default_avatar: "/images/default-avatar.png",
        is_reinitialize_started: false,
        initiated_operations: [],
        completed_operations: [],
        auto_downloaded_operation_ids: loadAutoDownloadedOperationIds(),
        dismissed_operation_ids: loadDismissedOperationIds(),
        // initiated_progresses: [],
        initiated_progresses: loadImportDraftProgress(),
        completed_progresses: [],
        loadingUserDataPromise: null,
        colorTheme: useColorTheme(),
        pre_support_admin: null,
        active_import_draft_id: null,
        productivity_worker_downline_profile_ids: null,
        loadingProductivityWorkerDownlineProfileIdsPromise: null,
        productivity_worker_downline_profile_ids_for_profile_id: null,
        productivityWorkerDownlineProfileIdsFetchGeneration: 0,
    }),
    getters: {
        is_client: (state) => state.profile?.type === "Client",
        is_not_client() {
            return !this.is_client;
        },
        isOnboardingComplete() {
            return Boolean(this.user?.signup_completed_at);
        },
        getTwilioIdentity: (state) => state.user?.twilio_identity,
        is_personal: (state) =>
            state.user_data?.profile?.organization?.type === "personal" ||
            !state.user_data?.user?.signup_completed_at,
        is_org_creator: (state) => {
            return (
                state.profile?.organization?.creator_user_id === state.user?.id
            );
        },
        is_org_admin: (state) => {
            return Boolean(state.profile?.permissions?.admin);
        },
        isOwner: (state) => {
            return Boolean(state.profile?.permissions?.owner);
        },
        isOwnerOrAdmin: (state) => {
            return (
                Boolean(state.profile?.permissions?.admin) ||
                Boolean(state.profile?.permissions?.owner)
            );
        },
        isProfileSetupCompleted() {
            const profile = this.profile;
            if (!profile) {
                return false;
            }

            return !!(
                profile.first_name?.trim() &&
                profile.last_name?.trim() &&
                profile.country_id &&
                profile.avatar_url
            );
        },
        isOrganizationSetupCompleted() {
            const organization = this.profile?.organization;
            if (!organization) {
                return false;
            }

            return !!(
                organization.organization_type &&
                organization.country_id &&
                organization.timezone &&
                organization.avatar_url
            );
        },
        owner: (state) => {
            const owner_id = state.profile?.organization?.creator_user_id;
            if (!owner_id) return null;
            if (state.user?.id === owner_id) return state.user;
            return state?.users?.find((user) => user.id === owner_id);
        },
        ownerProfile: (state) => {
            if (!state.owner) return null;
            return state.profiles.find(
                (profile) => profile.user_id === state.owner.id,
            );
        },
        usersProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                acc.set(profile.id, profile);
                return acc;
            }, new Map());
        },
        usersProfilesMapByUserId: (state) => {
            return state.profiles.reduce((acc, profile) => {
                acc.set(profile.user_id, profile);
                return acc;
            }, new Map());
        },
        inactiveUserIdMap: (state) => {
            const inactiveIdsMap = new Map();
            state.profiles.forEach((profile) => {
                if (
                    profile?.is_active === 0 &&
                    profile?.is_organization_user &&
                    !profile?.is_agent
                ) {
                    inactiveIdsMap.set(profile.id, profile.id);
                    inactiveIdsMap.set(profile.agent_id, profile.agent_id);
                }
            });
            return inactiveIdsMap;
        },
        inactiveUsers: (state) => {
            return state.profiles.filter((profile) => {
                let userProfile = profile;
                if (profile?.is_agent) {
                    userProfile = state.profiles.find(
                        (p) => profile.id === p.agent_id,
                    );
                }
                return (
                    userProfile?.is_active === 0 &&
                    userProfile?.is_organization_user
                );
            });
        },
        agent: (state) => state.profile?.agent,
        agentProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (profile?.is_agent) {
                    acc.set(profile.id, profile);
                }
                return acc;
            }, new Map());
        },
        agentUserProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (!profile?.is_agent) {
                    acc.set(profile.agent_id, profile);
                }
                return acc;
            }, new Map());
        },
        agentUserProfilesMapByEmail: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (!profile?.is_agent) {
                    acc.set(profile.email, profile);
                }
                return acc;
            }, new Map());
        },
        supportAdmin: (state) => state.user_data?.support_admin,
        downline: (state) => state.profile?.downline,
    },
    actions: {
        filterProfilesBy(search_key = "", profile_ids_at_start = []) {
            let members = this.profiles;

            if (search_key)
                members = members.filter(
                    (member) =>
                        member?.full_name
                            ?.toLowerCase()
                            .indexOf(search_key.toLowerCase()) > -1 ||
                        member?.email
                            ?.toLowerCase()
                            .indexOf(search_key.toLowerCase()) > -1,
                );

            if (!profile_ids_at_start?.length) return members;

            profile_ids_at_start
                .filter((profile_id) => parseInt(profile_id))
                ?.forEach((profile_id) => {
                    const workspace_onwer_index = members.findIndex(
                        (profile) => profile_id == profile.id,
                    );
                    if (workspace_onwer_index > 0) {
                        const owner_profile = members.splice(
                            workspace_onwer_index,
                            1,
                        )[0];
                        members.unshift(owner_profile);
                    }
                });

            return members;
        },
        resetCheckedProfiles() {
            this.profiles.forEach((profile) => {
                profile.checked = false;
                if (profile?.checkedOrder) {
                    delete profile?.checkedOrder;
                    if (profile?.non_registered) {
                        profile.invitation_sent = true;
                    }
                }
            });
        },
        setUserSearchKey(key = "") {
            this.user_search_key = key;
        },
        async fetchUserData() {
            if (this.user_data) return this.user_data;
            // ✅ If already fetching, just await the same promise
            if (this.loadingUserDataPromise) {
                return this.loadingUserDataPromise;
            }

            const alignStore = useAlignStore();
            this.loadingUserDataPromise = (async () => {
                try {
                    const userData = await axios.get(`/user`);
                    const {profile, user, state} = userData?.data;
                    alignStore.supportAdmin = userData?.data?.support_admin;
                    if (userData?.data?.support_admin) {
                        this.pre_support_admin = userData?.data?.support_admin;
                    }
                    // Sync color theme from backend to localStorage
                    if (profile?.color_theme) {
                        this.colorTheme.saveTheme(profile.color_theme);
                    }

                    this.setUpUserData(user, profile);
                    this.setUserData(userData.data);
                    return {profile, user, state};
                } catch (error) {
                    console.error("Error fetching user data", error);
                } finally {
                    this.loadingUserDataPromise = null;
                }
            })();
            return this.loadingUserDataPromise;
        },
        async initialize(is_reinitialize = false) {
            // Prevent multiple simultaneous initialization calls
            if (this.initializationPromise && !is_reinitialize) {
                return this.initializationPromise;
            }

            // Set promise IMMEDIATELY to prevent race conditions
            this.initializationPromise = (async () => {
                const app_state_store = useAppState();

                const data = await this.fetchUserData();
                const {profile, user, state} = data;

                if (profile?.type === "personal") {
                    this.user.signup_completed_at = user.signup_completed_at =
                        null;
                }

                useAppState().forceSignUpProcessIfNotCompleted(
                    user?.signup_completed_at,
                    user?.signup_index,
                );

                if (
                    profile?.type === "business" &&
                    profile?.permissions?.owner &&
                    profile?.organization?.creator_user_id === user?.id
                ) {
                    useAppState().setControlAsSelectedModule(false);
                } else if (profile?.type === "personal") {
                    useAppState().setControlAsSelectedModule(true);
                }

                await useUserDataKeyStore().fetchUserDataKey(profile);

                //app_state_store.updateConversationState(state.is_conversation_open);
                Utilities().toggleChatPopoverWithoutUpdatingState(
                    state.is_conversation_open,
                );

                const conversation_store = useConversation();
                app_state_store.togglePageLoader(false);
                if (state?.item_view_mode === "detail") {
                    app_state_store.is_detail_mode = true;
                }

                app_state_store.is_team_panel_dashboard_mode =
                    TEAM_PANEL_VIEW_MODES.DASHBOARD ===
                    state?.team_panel_view_mode;

                // Execute the main initialization logic

                return Promise.all([
                    useWorkspaceTree().loadWorkspaceCategories(),
                    axios.get("/conversations"),
                ])
                    .then(async ([workspaceCategories, convData]) => {

                        const conversations =
                            convData.data?.conversations.map(
                                (conversation) => {
                                    conversation.is_quick_load =
                                        state.conversation_id ===
                                        conversation.id;
                                    return conversation;
                                },
                            );

                        conversation_store.initialize(
                            conversations,
                            is_reinitialize,
                        );
                        //conversation_store.loadGroups();

                        this.createTwilioClientIfUserAlreadyLoggedin();
                        this.setDefaultViewForClients();
                        await conversation_store.loadConversationKaamfuRequests();

                        await app_state_store.fetchWorklineThenLoggedTime();
                        await app_state_store.initializeWith(state);
                        this.fetchCurrentProfileDownline();
                    })
                    .catch((error) => {
                        console.trace("Error on initialize");
                        if (error?.response?.status === 403)
                            app_state_store.setPermissionError(true);

                        // Clear the promise on error so it can be retried
                        this.initializationPromise = null;
                        throw error; // Re-throw so callers can handle the error
                    })
                    .finally(() => {
                        // Only clear promise on error (handled in catch block)
                        // On success, keep the promise for subsequent calls
                        app_state_store.stopRestoringState();
                    });
            })();

            return this.initializationPromise;
        },

        takeUserToCorrectRoute() {
            if (!this.isOnboardingComplete) {
                router.push({name: ROUTE_NAMES.ALIGN});
            } else {
                //TODO: we need to handle if user enter url directly in address bar(vue-router handles) default but restoring state
                //app_state_store.restoreRoute(state)
            }
        },

        setUpUserData(user, profile) {
            this.user = {...user, country: profile?.country_id};
            this.profile = profile;
            window.user = this.user;
            window.profile = this.profile;
            localStorage.setItem("kaamfu_user_id", this.user.id);

            if (this.profile.is_support_admin) {
                useConversation().fetchOtherOrganizationAq();
            }
        },

        setUpUserProfileData(profile) {
            this.profile = profile;
            window.profile = this.profile;
            localStorage.setItem("kaamfu_profile_id", this.profile.id);
            this.invalidateProductivityWorkerDownlineProfileIds();

            this.createTwilioClient();
        },
        setUpconversations(conversations) {
            initializeConversations();
        },
        createTwilioClient() {
            if (!this.profile.twilio_identity)
                return console.log("no twilio identity created");

            const {subscribeWithIdentity} = TwilioFactory();
            subscribeWithIdentity(this.profile.twilio_identity);
        } /* this is to pre initialize twilio client before loading any data
        as so speed up loading conversations */,
        createTwilioClientIfUserAlreadyLoggedin() {
            const logged_in_user_id = localStorage.getItem("kaamfu_user_id");
            if (!logged_in_user_id) return;
            const {subscribeWithIdentity} = TwilioFactory();
            subscribeWithIdentity(logged_in_user_id);
        },
        async fetchUsers() {
            if (this.users?.length) return this.users;

            return axios.get("/users").then(({data}) => {
                this.users = data;
                return data;
            });
        },
        async fetchUsersProfiles(options = {}) {
            const force = Boolean(options.force);

            if (!force && this.profiles?.length) {
                return this.profiles;
            }

            return axios.get("/user-profiles").then(({data}) => {
                this.profiles = data;
                this.updateInactiveProfiles();
                return data;
            });
        },
        setNewInvitedMember(val) {
            this.newAssignMember = false;
            this.newInvitedMember = val;
        },
        setNewAssignMember(val) {
            this.newInvitedMember = false;
            this.newAssignMember = val;
        },
        setAssignedUsers() {
            this.assigned_users = cloneDeep(this.users);
            this.assigned_users = this.assigned_users.map((user) => {
                if (this.isUserParticipant(user)) user.is_participant = true;
                return user;
            });
        },
        isUserParticipant(user) {
            const conversation_store = useConversation();
            const participants = conversation_store?.active?.participants;

            return (
                participants?.findIndex(
                    (participant) => participant.id == user.id,
                ) >= 0
            );
        },
        isCurrentProfile(profile_id) {
            return profile_id ? this.profile.id === profile_id : false;
        },
        isAuthorizedFor(permission) {
            return Boolean(this.profile?.permissions?.[permission]);
        },
        hasAdministrativePermission() {
            return (
                this.isAuthorizedFor("owner") || this.isAuthorizedFor("admin")
            );
        },
        hasPermissionForScreenshots(user_profile_id) {
            return this.profile?.permissions?.screenshots?.includes(
                user_profile_id,
            );
        },
        isOrganizationOwnerProfile(user_profile_id) {
            if (!user_profile_id) {
                return false;
            }

            return profileRepresentsOrganizationOwner(
                this.getUserProfile(user_profile_id),
                this.profile?.organization,
            );
        },
        hasOnlySubordinateTimerAQEvents(user_profile_id) {
            return this.profile?.permissions?.immediate_subordinate_aq?.includes(
                user_profile_id,
            );
        },
        getUserProfile(profile_id) {
            return this.profiles?.find((profile) => profile.id == profile_id);
        },
        getUserProfileByTwilioId(twilio_identity) {
            return this.profiles?.find(
                (profile) => profile.twilio_identity == twilio_identity,
            );
        },
        getUserProfileIdByTwilioId(twilio_identity) {
            return this.profiles?.find(
                (profile) => profile.twilio_identity == twilio_identity,
            )?.user_profile_id;
        },
        getCurrentUserProfile() {
            return this.profile;
        },
        getCurrentUserProfileId() {
            return this.profile?.id;
        },
        getCurrentUserOrganizationId() {
            return this.profile?.organization_id;
        },
        getCurrentUserOrganizationName() {
            return this.profile?.organization?.name;
        },
        userProfileSetActiveTask(task) {
            const item = useWorkspaceTree().getItemById(task.id);
            if (!item) {
                return;
            }

            const item_assignee_id = item?.version?.user_profile_id_assignee;
            const is_active_item = task?.status === TTL.Task_Start || task.status === TTL.Task_Resume;
            const is_paused_item = task.status === TTL.Task_Pause;

            if (item_assignee_id != this.getCurrentUserProfileId()) {
                return
            }
            let profile = this.profiles.find((p) => p.id === item_assignee_id);
            if (!profile) {
                return
            }

            if (is_active_item) {
                profile.active_task = item;
                profile.paused_task = null;
                return;
            }

            if (is_paused_item) {
                profile.paused_task = item;
                profile.active_task = null;
                return;
            }

            profile.paused_task = null;
            profile.active_task = null;

        },
        updateAccount() {
            axios.patch("/profiles", {
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                country_id: this.user?.country,
            });
        },
        async updatePartialProfile() {
            const old_profile = cloneDeep(this.profile);
            const old_display_name = old_profile.display_name;
            const old_first_name = old_profile.first_name;
            const old_last_name = old_profile.last_name;

            try {
                const {data} = await axios.patch(
                    `/profiles/${this.profile.id}`,
                    {
                        first_name: this.profile.first_name,
                        last_name: this.profile.last_name,
                        country_id: this.profile.country_id,
                        linkedin: this.profile.linkedin,
                    },
                );
                if (data?.data) {
                    const updated_profile = data.data;
                    if (updated_profile.first_name !== undefined) {
                        this.profile.first_name = updated_profile.first_name;
                    }
                    if (updated_profile.last_name !== undefined) {
                        this.profile.last_name = updated_profile.last_name;
                    }
                    if (updated_profile.country_id !== undefined) {
                        this.profile.country_id = updated_profile.country_id;
                    }
                    if (updated_profile.linkedin !== undefined) {
                        this.profile.linkedin = updated_profile.linkedin;
                    }
                    if (updated_profile.display_name !== undefined) {
                        this.profile.display_name =
                            updated_profile.display_name;
                    }
                    if (updated_profile.full_name !== undefined) {
                        this.profile.full_name = updated_profile.full_name;
                    }

                    // Update conversation participants immediately if name changed
                    const name_changed =
                        old_first_name !== updated_profile.first_name ||
                        old_last_name !== updated_profile.last_name ||
                        old_display_name !== updated_profile.display_name;

                    if (name_changed) {
                        useConversation().updateConversationParticipant(
                            this.profile,
                        );
                    }
                }
            } catch (error) {
                console.log("Error updating profile:", error);
            }
        },
        updateProfile(profile) {
            axios
                .patch(`/profiles/${profile.id}`, profile)
                .then(({data}) => {
                });
        },
        updateProfileAvatar(profileId, base64) {
            return axios
                .post(`/profiles/${profileId}/avatar`, {avatar: base64})
                .then(({data}) => {
                    if (this.profile.id === profileId) {
                        this.profile.avatar_url = data.data.url;
                    }
                    return data;
                });
        },
        setUserData(val) {
            this.user_data = val;
            this.own_profiles = val.profiles;

            if (val.profile.type === "personal")
                useAppState().setControlAsSelectedModule(true);
        },

        async getProfileById(profile_id) {
            try {
                const {data} = await axios.get(`user-profiles/${profile_id}`);
                return data.data;
            } catch (error) {
                console.error("getProfileById failed", error);
                return {};
            }
        },

        async replaceUserProfile(newProfile) {
            if (!newProfile?.id) {
                return;
            }

            let profile = this.profiles.find((p) => p.id === newProfile.id);
            if (!profile) {
                await this.fetchUsersProfiles();
                profile = this.profiles.find((p) => p.id === newProfile.id);
            }

            if (!profile) {
                this.profiles.push(newProfile);
                this.updateInactiveProfiles();
                profile = newProfile;
            }

            Object.keys(newProfile).forEach((key) => {
                if (key !== "avatar_url") {
                    profile[key] = newProfile[key];
                }
            });

            if (Object.hasOwn(newProfile, "is_active")) {
                profile.is_active = Number(newProfile.is_active);
            }

            const img_url = newProfile.avatar_url ?? profile.avatar_url;
            if (img_url) {
                const profileId = profile.id;
                const img = new Image();
                img.onload = () => {
                    const idx = this.profiles.findIndex(
                        (p) => p.id === profileId,
                    );
                    if (idx >= 0) {
                        this.profiles[idx].avatar_url = img_url;
                    }
                };
                img.src = img_url;
            }
        },

        async replaceProfile(new_profile) {
            if (!new_profile?.id) {
                return;
            }

            let profile_index = this.profiles.findIndex(
                (p) => p.id === new_profile.id,
            );

            if (profile_index === -1) {
                profile_index = this.profiles.findIndex(
                    (p) => p.email === new_profile.email,
                );
            }

            const is_current = this.isCurrentProfile(new_profile.id);
            let old_profile = null;

            if (profile_index >= 0) {
                old_profile = this.profiles[profile_index];
                await this.replaceUserProfile(new_profile);
                if (is_current) {
                    this.replaceCurrentProfile(new_profile);
                }
            } else {
                this.profiles.push({...new_profile});
                if (is_current) {
                    this.replaceCurrentProfile(new_profile);
                }
            }

            let has_support_admin_modified = false;

            if (!new_profile?.is_active && new_profile?.is_support_admin) {
                const remove_idx = this.profiles.findIndex(
                    (p) => p.id === new_profile.id,
                );
                if (remove_idx >= 0) {
                    this.profiles.splice(remove_idx, 1);
                }
                this.user_data.support_admin = null;
                useConversation().removeSupportAdminDMConversation(
                    new_profile.id,
                );
                has_support_admin_modified = true;
            }

            if (
                old_profile &&
                new_profile.display_name !== old_profile.display_name
            ) {
                useConversation().updateConversationParticipant(new_profile);
            }

            if (new_profile?.is_active && new_profile.is_support_admin) {
                this.user_data.support_admin = new_profile;
                has_support_admin_modified = true;
            }

            if (useAppState().is_directory) {
                useWorkstationConnectStore().fetchWscUsers();
            }

            if (
                has_support_admin_modified &&
                (this.isOwner || this.is_org_admin)
            ) {
                useRolesAndPermissions().refetchRolePermissions();
            }
        },
        async replaceCurrentProfile(new_profile) {
            this.user_data.profile = this.profile = {
                ...this.profile,
                ...new_profile,
            };
        },

        // async addCompletedOperation(completed_operation) {
        //     const itemIndex = this.initiated_operations.findIndex(
        //         (item) => item.id === completed_operation.id,
        //     );
        //     if (itemIndex !== -1) {
        //         this.initiated_operations.splice(itemIndex, 1);
        //     }
        //
        //     this.completed_operations.push(completed_operation);
        // },

        async addCompletedOperation(completed_operation) {
            const initiated_item = this.initiated_operations.find(
                (item) => item.id === completed_operation.id,
            );
            const itemIndex = this.initiated_operations.findIndex(
                (item) => item.id === completed_operation.id,
            );
            if (itemIndex !== -1) {
                this.initiated_operations.splice(itemIndex, 1);
            }

            const normalized_operation = {
                ...completed_operation,
                created_at:
                    completed_operation.created_at ??
                    initiated_item?.created_at ??
                    completed_operation.updated_at ??
                    new Date().toISOString(),
                updated_at:
                    completed_operation.updated_at ?? new Date().toISOString(),
            };

            const completedIndex = this.completed_operations.findIndex(
                (item) => item.id === completed_operation.id,
            );

            if (completedIndex !== -1) {
                this.completed_operations.splice(
                    completedIndex,
                    1,
                    normalized_operation,
                ); // replace
            } else {
                this.completed_operations.push(normalized_operation); // add new
            }
        },

        async addInitiatedOperation(initiated_operation) {
            this.initiated_operations.push({
                ...initiated_operation,
                created_at:
                    initiated_operation.created_at ??
                    new Date().toISOString(),
            });
        },

        async addCancellableExportOperation(initiated_operation) {
            await this.addInitiatedOperation({
                ...initiated_operation,
                can_cancel: true,
            });
        },

        removeInitiatedOperationById(id) {
            const itemIndex = this.initiated_operations.findIndex(
                (item) => item.id === id,
            );
            if (itemIndex !== -1) {
                this.initiated_operations.splice(itemIndex, 1);
            }
        },

        dismissCompletedOperation(id) {
            if (!id) {
                return;
            }

            const completedIndex = this.completed_operations.findIndex(
                (item) => item.id === id,
            );
            if (completedIndex !== -1) {
                this.completed_operations.splice(completedIndex, 1);
            }

            this.dismissed_operation_ids = rememberDismissedOperationId(
                id,
                this.dismissed_operation_ids,
            );
        },

        dismissCompletedProgress(id) {
            if (!id) {
                return;
            }

            const completedIndex = this.completed_progresses.findIndex(
                (item) => item.id === id,
            );
            if (completedIndex !== -1) {
                this.completed_progresses.splice(completedIndex, 1);
            }

            this.dismissed_operation_ids = rememberDismissedOperationId(
                id,
                this.dismissed_operation_ids,
            );
        },

        pruneExpiredCompletedOperations(now = Date.now()) {
            this.completed_operations = this.completed_operations.filter(
                (operation) =>
                    isCompletedOperationVisible(operation, {
                        dismissedIds: this.dismissed_operation_ids,
                        now,
                    }),
            );

            this.completed_progresses = this.completed_progresses.filter(
                (progress) =>
                    isCompletedOperationVisible(progress, {
                        dismissedIds: this.dismissed_operation_ids,
                        now,
                    }),
            );
        },

        finishExportAndDownload(operation, { manual = false } = {}) {
            if (!operation?.id) {
                return;
            }

            const initiated_item = this.initiated_operations.find(
                (item) => item.id === operation.id,
            );

            this.removeInitiatedOperationById(operation.id);
            const existing_completed = this.completed_operations.find(
                (item) => item.id === operation.id,
            );
            const normalized_operation = {
                id: operation.id,
                url: operation.url ?? existing_completed?.url ?? null,
                name:
                    operation.name ??
                    existing_completed?.name ??
                    initiated_item?.name ??
                    null,
                status: operation.status ?? existing_completed?.status ?? "completed",
                created_at:
                    operation.created_at ??
                    existing_completed?.created_at ??
                    initiated_item?.created_at ??
                    new Date().toISOString(),
                updated_at:
                    operation.updated_at ??
                    existing_completed?.updated_at ??
                    new Date().toISOString(),
            };

            const completedIndex = this.completed_operations.findIndex(
                (item) => item.id === operation.id,
            );
            if (completedIndex !== -1) {
                this.completed_operations.splice(
                    completedIndex,
                    1,
                    normalized_operation,
                );
            } else {
                this.completed_operations.push(normalized_operation);
            }

            if (
                !shouldDownloadExport(
                    this.auto_downloaded_operation_ids,
                    operation.id,
                    { manual },
                )
            ) {
                return;
            }

            if (
                !hasAutoDownloadedOperationId(
                    this.auto_downloaded_operation_ids,
                    operation.id,
                )
            ) {
                this.auto_downloaded_operation_ids =
                    rememberAutoDownloadedOperationId(
                        operation.id,
                        this.auto_downloaded_operation_ids,
                    );
            }

            downloadGeneratedReport(operation.id, operation.name);

            if (!manual) {
                useWorkspace().showCompletedExportShiftbarNotice(
                    buildCompletedExportShiftbarNotice(normalized_operation),
                );
            }
        },

        async cancelGenerateReportProgress(id) {
            this.removeInitiatedOperationById(id);

            try {
                await axios.post(`/reports/generate-progresses/${id}/cancel`);
            } catch (error) {
                console.error("failed to cancel generate report progress.");
            }
        },

        async discardActiveImportProgress(id) {
            if (!id || isImportDraftId(id)) {
                return;
            }

            // Analysis progress ids are reused for the live import job. Never
            // remove/discard a still-running import that already owns this id.
            if (isLiveImportProgressId(this.initiated_progresses, id)) {
                return;
            }

            const linkedDraft = findImportDraftByAnalysisProgressId(
                this.initiated_progresses,
                id,
            );

            this.removeInitiatedProgressById(id);

            if (linkedDraft?.id) {
                this.removeInitiatedProgressById(linkedDraft.id);
            }

            unsubscribeImportProgressChannel(id);

            try {
                await axios.post(`/import-progress/${id}/discard`);
            } catch (error) {
                console.error("failed to discard import progress.");
            }
        },

        async syncProgressesOnReconnect() {
            await Promise.all([
                this.syncGenerateReportProgresses(),
                this.syncActiveImportProgresses(),
            ]);
        },

        async syncGenerateReportProgresses() {
            const ids = this.initiated_operations
                .map((operation) => operation.id)
                .filter(Boolean);

            try {
                const {data} = await axios.get("/reports/generate-progresses", {
                    params: ids.length ? {ids} : {},
                });
                const payload = data?.data ?? {};
                const result = applyGenerateReportProgressCatchUp({
                    initiated_operations: this.initiated_operations,
                    completed_operations: this.completed_operations,
                    initiated: payload.initiated ?? [],
                    completed: payload.completed ?? [],
                    failed: payload.failed ?? [],
                    cancelled: payload.cancelled ?? [],
                    auto_downloaded_operation_ids:
                    this.auto_downloaded_operation_ids,
                    dismissed_operation_ids: this.dismissed_operation_ids,
                });

                this.initiated_operations = result.initiated_operations;
                this.completed_operations = result.completed_operations;

                for (const download of result.downloads) {
                    this.finishExportAndDownload(download);
                }

                for (const notification of result.notifications) {
                    if (notification.type === "error") {
                        Utilities().showErrorSnackbar(
                            notification.message,
                            null,
                            "error",
                            10000,
                        );
                    } else {
                        Utilities().showInfoSnackbar(
                            notification.message,
                            null,
                            "info",
                            10000,
                        );
                    }
                }
            } catch (error) {
                console.error("failed to sync generate report progresses.");
            }
        },

        async syncActiveImportProgresses() {
            try {
                const ids = this.initiated_progresses
                    .map((progress) => progress.id)
                    .filter(Boolean);
                const {data} = await axios.get("/import-progress/active", {
                    params: ids.length ? {ids} : {},
                });

                if (
                    data?.success &&
                    (data.initiated?.length || data.completed?.length)
                ) {
                    this.hydrateActiveImportProgresses(
                        data.initiated ?? [],
                        data.completed ?? [],
                    );
                }
            } catch (error) {
                console.error("failed to sync import progresses.");
            }
        },

        addProfile(new_profile) {
            const profile_index = this.profiles.findIndex(
                (profile) => profile.email === new_profile.email,
            );
            if (profile_index !== -1) {
                this.profiles.splice(profile_index, 1, new_profile);
            } else {
                this.profiles.push(new_profile);
            }
        },

        /** Drop pending-invite business + Kai profiles after org invitation revoke live-sync. */
        removeOrganizationInvitationRevokedMember({
                                                      user_id,
                                                      user_profile_id,
                                                      agent_user_profile_id,
                                                      agent_user_id,
                                                  }) {
            const profile_ids = new Set(
                [user_profile_id, agent_user_profile_id].filter(
                    (id) => id != null && id !== "",
                ).map(Number),
            );
            this.profiles = this.profiles.filter(
                (p) => !profile_ids.has(Number(p.id)),
            );

            const candidate_user_ids = new Set(
                [user_id, agent_user_id].filter(
                    (id) => id != null && id !== "",
                ).map(Number),
            );
            for (const uid of candidate_user_ids) {
                const still_referenced = this.profiles.some(
                    (p) => Number(p.user_id) === uid,
                );
                if (!still_referenced) {
                    this.users = this.users.filter((u) => Number(u.id) !== uid);
                }
            }

            this.updateInactiveProfiles();
        },
        updateProfileTask(item) {
            if (item?.id == null) {
                return;
            }

            const itemId = item.id;
            const name = item.name;

            const taskMatchesItem = (task) =>
                task && (task.id == itemId || task.item_id == itemId);

            const applyName = (task) => {
                if (taskMatchesItem(task)) {
                    task.name = name;
                }
            };

            for (const profile of this.profiles) {
                applyName(profile?.active_task);
                applyName(profile?.paused_task);
            }

            applyName(this.profile?.active_task);
            applyName(this.profile?.paused_task);
        },
        setDefaultViewForClients() {
            if (this.is_not_client) return;

            const workspace_store = useWorkspace();
            workspace_store.active_view_mode = WORK_PANEL_VIEW_MODE_FULL;
        },

        isCurrentUser(twilio_identity) {
            return this.user.twilio_identity === twilio_identity;
        },

        isNotCurrentUser(twilio_identity) {
            return this.user.twilio_identity !== twilio_identity;
        },

        async reInitialize() {
            if (this.is_reinitialize_started) {
                return;
            }
            try {
                this.is_reinitialize_started = true;
                console.log("ReInitialize started at", Date().toString());
                this.initialize(true);
                // Team Panel start
                if (useTeamPanel().is_prepared) {
                    await useTeamPanel().reInitializeTeamPanel();
                }
                console.log("ReInitialize ended at", Date().toString());
                this.is_reinitialize_started = false;
                // Team Panel end
            } catch (e) {
                console.log("ReInitialize failed", e, Date().toString());
                this.is_reinitialize_started = false;
            }
        },

        increaseTwilioConversationCount() {
            this.profile.twilio_conversations_count += 1;
        },
        setTwilioConversationCount(count) {
            this.profile.twilio_conversations_count = count;
        },
        decreaseTwilioConversationCount() {
            this.profile.twilio_conversations_count -= 1;
        },
        canInviteToOrganization() {
            return this.getCurrentUserProfile().type === "business";
        },
        async loadUserState() {
            const res = await axios.get(`/user`);
            this.setUserData(res.data);
            useAppState().forceSignUpProcessIfNotCompleted(
                res.data?.user?.signup_completed_at,
            );
        },
        canShowAsigner(user_profile) {
            if (!user_profile || !this.profile?.organization?.creator_user_id) {
                return false;
            }

            if (this.profile?.organization?.creator_user_id === this.user?.id) {
                return user_profile.email !== this.user.email;
            }

            const subordinates = useTeamPanel().getSubordinatesByProfile(
                this.profile.id,
            );
            const user = subordinates.find(
                (user) => user.user_profile_id === user_profile.user_profile_id,
            );
            return !!user;
        },
        async enableAdminSupport() {
            try {
                const response = await axios.post(
                    "/organizations/invite/support-admin",
                );

                const isExist = this.profiles.find(
                    (profile) => profile.id === response.data.data.id,
                );
                if (!isExist) {
                    this.profiles.push(response.data.data);
                }
                this.user_data.support_admin = response.data.data;
                return response.data.data;
            } catch (error) {
                Utilities().showErrorSnackbar(
                    error?.response?.data?.message ??
                    "Something went wrong. Please try again later.",
                );
                return null;
            }
        },

        async disableAdminSupport() {
            try {
                await axios.post("/organizations/un-invite/support-admin");

                const supportAdminData =
                    this.user_data.support_admin ?? this.pre_support_admin;
                this.user_data.support_admin = null;
                const isExist = this.profiles.find(
                    (profile) => profile.id === supportAdminData?.id,
                );
                if (isExist) {
                    this.profiles.splice(this.profiles.indexOf(isExist), 1);
                }
            } catch (error) {
            }
        },

        getProfilesByIds(profile_ids = [], preserve_sorting = false) {
            let members = this.profiles;

            if (!profile_ids?.length) return [];

            let response = members.filter((profile) =>
                profile_ids.includes(profile.id),
            );

            if (preserve_sorting) {
                response = profile_ids
                    .map((id) => response.find((profile) => profile.id === id))
                    .filter(Boolean);
            }

            return response;
        },
        updateInactiveProfiles() {
            this.inactive_profiles = this.profiles
                .filter((profile) => profile.is_active === 0)
                .map((profile) => profile.id);

            return this.inactive_profiles;
        },
        updateCurrentUserPermissions(permissions) {
            this.profile.permissions = permissions;
        },
        updateCurrentUserStatus(obj) {
            const profile = this.profiles.find(
                (profile) => profile.id === this.profile.id,
            );
            if (!profile) return;
            Object.keys(obj ?? {}).forEach((key) => {
                const value = obj[key];
                profile[key] = value;
                this.profile[key] = value;
            });
        },
        getCurrentUserStatus() {
            const profile = this.profiles.find(
                (profile) => profile.id === this.profile.id,
            );
            return profile;
        },
        findProfileById(profile_id) {
            return this.profiles.find((profile) => profile.id === profile_id);
        },
        updateUserBasicDetails(userLatestDetails) {
            this.profile = {
                ...this.profile,
                ...userLatestDetails,
            };
            this.user = {
                ...this.user,
                ...userLatestDetails,
            };
        },
        isMainKaamfuOrganization() {
            return this.user_data.is_kaamfu_organization;
        },
        removeOtherOrganizationProfile(user_profile) {
            // check if its current user profile. If so, do not remove. Its automatically handled by api middleware.
            if (user_profile.id == this.getCurrentUserProfileId()) {
                return;
            }
            const profile_index = this.own_profiles.findIndex(
                (profile) =>
                    profile.id === user_profile.id &&
                    profile.organization_id == user_profile.organization_id,
            );

            if (profile_index >= 0) {
                this.own_profiles.splice(profile_index, 1);
            }
        },
        addOtherOrganizationProfile(user_profile) {
            // User information is not shared across the organization. Therefore, better to reload all profiles in background.
            const profile_index = this.own_profiles.findIndex(
                (profile) =>
                    profile.id === user_profile.id &&
                    profile.organization_id == user_profile.organization_id,
            );

            if (profile_index == -1) {
                this.fetchUserData();
            }
        },
        async fetchImportCount(payload) {
            try {
                const response = await axios.post(
                    `/save-profiles/import-count`,
                    payload,
                );
                return response.data.data;
            } catch (error) {
                return 0;
            }
        },
        async uploadTeam(payload) {
            try {
                const response = await axios.post(
                    `/save-profiles/team`,
                    payload,
                );
                Utilities().showSuccessSnackbar(response.data.message);
                return response.data.data;
            } catch (error) {
                Utilities().showErrorSnackbar(
                    error?.response?.data?.message ??
                    "Something went wrong. Please try again later.",
                );
                return null;
            }
        },

        async addInitiatedProgress(initiated_progress) {
            let existingIndex = this.initiated_progresses.findIndex(
                (p) => p.id === initiated_progress.id,
            );

            if (existingIndex === -1 && isImportDraftId(initiated_progress.id)) {
                const analysisProgressId = getImportDraftAnalysisProgressId(
                    initiated_progress.meta?.draft_state,
                );

                if (analysisProgressId) {
                    existingIndex = this.initiated_progresses.findIndex(
                        (p) =>
                            isImportDraftId(p.id) &&
                            getImportDraftAnalysisProgressId(
                                p.meta?.draft_state,
                            ) === analysisProgressId,
                    );
                }
            }

            if (existingIndex !== -1) {
                const existingId = this.initiated_progresses[existingIndex].id;
                Object.assign(
                    this.initiated_progresses[existingIndex],
                    initiated_progress,
                );
                this.initiated_progresses[existingIndex].id = existingId;
            } else {
                this.initiated_progresses.push(initiated_progress);
            }

            if (isImportDraftId(initiated_progress.id)) {
                const item =
                    existingIndex !== -1
                        ? this.initiated_progresses[existingIndex]
                        : initiated_progress;
                upsertImportDraftProgress(
                    item,
                    this.initiated_progresses.filter((p) =>
                        isImportDraftId(p.id),
                    ),
                );
                return;
            }

            const linkedDraft = findImportDraftByAnalysisProgressId(
                this.initiated_progresses,
                initiated_progress.id,
            );

            if (linkedDraft?.id) {
                this.removeInitiatedProgressById(linkedDraft.id);
            }
        },

        removeInitiatedProgressById(id) {
            const idx = this.initiated_progresses.findIndex((p) => p.id === id);
            if (idx !== -1) {
                this.initiated_progresses.splice(idx, 1);
            }
            if (isImportDraftId(id)) {
                removeImportDraftProgress(
                    id,
                    this.initiated_progresses.filter((p) => isImportDraftId(p.id)),
                );
            }
            if (this.active_import_draft_id === id) {
                this.clearActiveImportDraftId();
            }
        },

        setActiveImportDraftId(id) {
            this.active_import_draft_id = id;
        },
        clearActiveImportDraftId() {
            if (this.active_import_draft_id) {
                this.active_import_draft_id = null;
            }
        },

        updateInitiatedProgressById(id, patch) {
            const item = this.initiated_progresses.find((p) => p.id === id);
            if (!item) {
                return false;
            }
            Object.assign(item, patch);
            if (isImportDraftId(id)) {
                upsertImportDraftProgress(
                    item,
                    this.initiated_progresses.filter((p) => isImportDraftId(p.id)),
                );
            }
            return true;
        },

        async addCompletedProgress(completed_progress) {
            const completedIndex = this.completed_progresses.findIndex(
                (item) => item.id === completed_progress.id,
            );

            if (completedIndex !== -1) {
                this.completed_progresses.splice(
                    completedIndex,
                    1,
                    completed_progress,
                ); // replace
            } else {
                this.completed_progresses.push(completed_progress); // add new
            }
        },

        async getInitiatedProgresses() {
            return this.initiated_progresses;
        },

        async getCompletedProgresses() {
            return this.completed_progresses;
        },

        /**
         * Hydrate initiated_progresses and completed_progresses from API (e.g. after page refresh).
         * Merges by id so existing in-session items are not duplicated.
         */
        hydrateActiveImportProgresses(initiated = [], completed = []) {
            initiated.forEach((p) => {
                subscribeImportProgressChannel(p.id);

                const idx = this.initiated_progresses.findIndex(
                    (item) => item.id === p.id,
                );
                if (idx === -1) {
                    this.initiated_progresses.push(p);
                } else {
                    Object.assign(this.initiated_progresses[idx], p);
                }

                if (!isImportDraftId(p.id)) {
                    const linkedDraft = findImportDraftByAnalysisProgressId(
                        this.initiated_progresses,
                        p.id,
                    );

                    if (linkedDraft?.id) {
                        this.removeInitiatedProgressById(linkedDraft.id);
                    }
                }
            });

            completed.forEach((p) => {
                if (hasDismissedOperationId(this.dismissed_operation_ids, p.id)) {
                    return;
                }

                const initiatedIdx = this.initiated_progresses.findIndex(
                    (item) => item.id === p.id,
                );
                if (initiatedIdx !== -1) {
                    Object.assign(this.initiated_progresses[initiatedIdx], p);
                    return;
                }

                const completedIdx = this.completed_progresses.findIndex(
                    (item) => item.id === p.id,
                );
                if (completedIdx === -1) {
                    this.completed_progresses.push(p);
                } else {
                    Object.assign(this.completed_progresses[completedIdx], p);
                }
            });

            this.pruneExpiredCompletedOperations();
        },

        getProfileAndAgent(profile) {
            if (profile?.is_agent) {
                return {
                    user: this.getAgentUserProfileByAgentId(profile?.id),
                    agent: profile,
                };
            }
            return {
                user: profile,
                agent: this.getAgentProfileById(profile?.agent_id),
            };
        },

        getAgentProfileById(profileId) {
            return this.agentProfilesMap.get(profileId);
        },

        getAgentUserProfileByAgentId(profileId) {
            return this.agentUserProfilesMap.get(profileId);
        },
        getUserProfileById(profileId) {
            if (profileId == null) {
                return null;
            }
            const numericId = Number(profileId);
            const key = !Number.isNaN(numericId) ? numericId : profileId;
            return this.usersProfilesMap.get(key);
        },
        getUserProfileWithCountryId(profileId) {
            const profile = this.getUserProfileById(profileId);
            if (!profile) return null;
            const country = useAppState().getCountryById(profile.country_id);
            return {...profile, country};
        },
        isDisassociatedUser(profileId) {
            return this.inactiveUserIdMap.has(profileId);
        },
        getUserProfileByUserId(userId) {
            return this.usersProfilesMapByUserId.get(userId);
        },
        async fetchAndSetUserData() {
            try {
                const {data} = await axios.get("/user");
                this.setUpUserData(data.user, data.profile);
                this.setUserData(data);
            } catch (error) {
                console.error("setUserDataFromServer failed", error);
            }
        },

        async fetchCurrentProfileDownline() {
            if (!this.profile) {
                return;
            }
            try {
                const {data} = await axios.get("/downline-hierarchy");
                this.profile.downline = data.data;
            } catch (err) {
                console.error("Failed to fetch downline hierarchy", err);
            }
        },

        invalidateProductivityWorkerDownlineProfileIds() {
            this.productivity_worker_downline_profile_ids = null;
            this.productivity_worker_downline_profile_ids_for_profile_id = null;
            this.productivityWorkerDownlineProfileIdsFetchGeneration += 1;
        },

        async fetchProductivityWorkerDownlineProfileIds(force = false) {
            const profile_id = this.profile?.id;
            if (!profile_id) {
                return [];
            }

            if (
                !force &&
                this.loadingProductivityWorkerDownlineProfileIdsPromise === null &&
                this.productivity_worker_downline_profile_ids_for_profile_id ===
                    profile_id &&
                Array.isArray(this.productivity_worker_downline_profile_ids)
            ) {
                return this.productivity_worker_downline_profile_ids;
            }

            if (
                !force &&
                this.loadingProductivityWorkerDownlineProfileIdsPromise
            ) {
                return this.loadingProductivityWorkerDownlineProfileIdsPromise;
            }

            const generation =
                ++this.productivityWorkerDownlineProfileIdsFetchGeneration;

            const isStale = () =>
                generation !==
                    this.productivityWorkerDownlineProfileIdsFetchGeneration ||
                this.profile?.id !== profile_id;

            this.loadingProductivityWorkerDownlineProfileIdsPromise =
                (async () => {
                    try {
                        const { data } = await axios.get(
                            "/user/productivity-worker-downline-profile-ids",
                        );
                        const profile_ids = data?.data?.profile_ids ?? [];
                        if (!isStale()) {
                            this.productivity_worker_downline_profile_ids =
                                profile_ids;
                            this.productivity_worker_downline_profile_ids_for_profile_id =
                                profile_id;
                        }

                        return profile_ids;
                    } catch (err) {
                        console.error(
                            "Failed to fetch productivity worker downline profile ids",
                            err,
                        );
                        if (!isStale()) {
                            this.productivity_worker_downline_profile_ids = [];
                            this.productivity_worker_downline_profile_ids_for_profile_id =
                                profile_id;
                        }

                        return [];
                    } finally {
                        if (
                            generation ===
                            this.productivityWorkerDownlineProfileIdsFetchGeneration
                        ) {
                            this.loadingProductivityWorkerDownlineProfileIdsPromise =
                                null;
                        }
                    }
                })();

            return this.loadingProductivityWorkerDownlineProfileIdsPromise;
        },

        clearPausedTaskByProfileId(profileId, itemId) {
            if (!profileId || !itemId) {
                return;
            }

            const profile = this.profiles?.find((p) => p.id == profileId);
            if (profile && profile.paused_task?.id == itemId) {
                profile.paused_task = null;
            }

            if (
                this.profile?.id == profileId &&
                this.profile?.paused_task?.id == itemId
            ) {
                this.profile.paused_task = null;
            }
        }
    },
});
